// Generated by Claude Code
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { State, useComponentState } from '#core/state/State.ts'
import ApiUsersApi from '#core/api/auth/ApiUsersApi.ts'
import type ApiUser from '#core/entities/auth/api-users/ApiUser.ts'
import type ApiUserCreated from '#core/entities/auth/api-users/ApiUserCreated.ts'
import type RoleDetail from '#core/entities/auth/roles/RoleDetail.ts'
import { showToast } from '#core/notifications/toast.ts'
import { CardModal } from '#components/primitives/components/card-modal.tsx'
import { FormField } from '#components/primitives/forms/form-field.tsx'
import { FormFooterActions } from '#components/primitives/components/form-footer-actions.tsx'
import {
  RoleAssignmentFields,
  type AssignmentSelection,
  emptyAssignment,
  assignmentIsValid,
  assignmentPayload,
  selectionFromMember,
} from '#components/domain/auth/auth/shared/role-assignment-fields.tsx'
import {
  PermissionErrorAlert,
  permissionErrorBody,
  type PermissionErrorBody,
} from '#components/domain/auth/auth/shared/permission-error.tsx'

const apiUsersApi = new ApiUsersApi()

interface ApiUserEditModalProps {
  isOpen: boolean
  /** The API user to edit; null opens the create form. */
  apiUser: ApiUser | null
  roles: RoleDetail[]
  onClose: () => void
  onCreated: (created: ApiUserCreated) => void
  onSaved: () => void
}

class ApiUserEditState extends State<ApiUserEditState> {
  name = ''
  assignment: AssignmentSelection = emptyAssignment()
  saving = false
  permError: PermissionErrorBody | null = null

  reset(apiUser: ApiUser | null): void {
    this.reactTo.name = apiUser?.name ?? ''
    this.reactTo.assignment = apiUser
      ? selectionFromMember(apiUser.role_id ?? null, apiUser.personal_permissions ?? [])
      : emptyAssignment()
    this.reactTo.saving = false
    this.reactTo.permError = null
  }

  async save(apiUser: ApiUser | null, props: ApiUserEditModalProps): Promise<void> {
    const payload = assignmentPayload(this.assignment)
    if (!payload) return
    this.reactTo.saving = true
    this.reactTo.permError = null
    const name = this.name.trim()
    try {
      if (apiUser) {
        if (name !== apiUser.name) await apiUsersApi.renameApiUser(apiUser.id, name)
        await apiUsersApi.updateAssignment(apiUser.id, payload.role_id, payload.personal_permissions)
        props.onSaved()
      } else {
        const created = await apiUsersApi.createApiUser(name, payload.role_id, payload.personal_permissions)
        if (created) props.onCreated(created)
      }
      props.onClose()
    } catch (e) {
      const permError = permissionErrorBody(e)
      if (permError) {
        this.reactTo.permError = permError
      } else {
        showToast(i18n.t('usersTeams.saveError', { ns: 'settings' }), 'danger')
      }
    } finally {
      this.reactTo.saving = false
    }
  }
}

/** Super-user form for creating an API user or editing its name and role assignment. */
export function ApiUserEditModal(props: ApiUserEditModalProps) {
  const { isOpen, apiUser, roles, onClose } = props
  const { t } = useTranslation('settings')
  const state = useComponentState(ApiUserEditState)
  const isEdit = apiUser != null

  useEffect(() => {
    if (isOpen) state.reset(apiUser)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, apiUser?.id])

  const footer = (
    <FormFooterActions
      onCancel={onClose}
      onSubmit={() => void state.save(apiUser, props)}
      submitting={state.saving}
      submitDisabled={!state.name.trim() || !assignmentIsValid(state.assignment)}
      submitLabel={isEdit ? t('shared.actions.save') : t('shared.actions.create')}
      submittingLabel={isEdit ? t('shared.actions.saving') : t('shared.actions.creating')}
    />
  )

  return (
    <CardModal
      isOpen={isOpen}
      title={isEdit ? t('usersTeams.apiUsers.editTitle') : t('usersTeams.actions.addApiUser')}
      footer={footer}
      onClose={onClose}
    >
      <FormField label={t('shared.fields.name')} htmlFor="api-user-name" required>
        <input
          id="api-user-name"
          type="text"
          className="form-control"
          value={state.name}
          onChange={e => { state.name = e.target.value }}
        />
      </FormField>

      <RoleAssignmentFields
        roles={roles}
        value={state.assignment}
        onChange={next => { state.reactTo.assignment = next }}
        idPrefix="api-user"
        disabled={state.saving}
      />

      {state.permError && <div className="mt-3"><PermissionErrorAlert body={state.permError} /></div>}
    </CardModal>
  )
}
